import { FolderItem } from '@/models'

import { CheckboxState, FlattenedOption, SelectOptionValue, SelectTreeOption } from './select.types'

const hasMatchInSubtree = <V extends SelectOptionValue>(
  options: Array<SelectTreeOption<V>>,
  query: string,
): boolean => {
  for (const option of options) {
    if (option.label.toLowerCase().includes(query)) return true
    if (option.children && hasMatchInSubtree(option.children, query)) return true
  }
  return false
}

export const collectDefaultExpandedKeys = <V extends SelectOptionValue>(
  options: Array<SelectTreeOption<V>>,
): Set<V> => {
  const keys = new Set<V>()
  const traverse = (items: Array<SelectTreeOption<V>>) => {
    for (const item of items) {
      if (item.defaultExpanded) keys.add(item.value)
      if (item.children) traverse(item.children)
    }
  }
  traverse(options)
  return keys
}

export const collectExpandedKeys = <V extends SelectOptionValue>(
  options: Array<SelectTreeOption<V>>,
  query: string,
): Set<V> => {
  const keys = new Set<V>()

  for (const option of options) {
    if (option.children && option.children.length > 0) {
      const childKeys = collectExpandedKeys(option.children, query)

      const hasMatchingDescendant = childKeys.size > 0 || hasMatchInSubtree(option.children, query)

      if (hasMatchingDescendant) {
        // A descendant matched — expand this node and merge child keys
        keys.add(option.value)
        childKeys.forEach((k) => keys.add(k))
      }

      // Also expand if this node itself matches and has children
      if (option.label.toLowerCase().includes(query)) {
        keys.add(option.value)
      }
    }
  }

  return keys
}

// Note: Recursively search all options including children to find selected ones
export const findOptionsFlat = <V extends SelectOptionValue>(
  options: Array<SelectTreeOption<V>>,
  predicate: (opt: SelectTreeOption<V>) => boolean,
): Array<SelectTreeOption<V>> => {
  const result: Array<SelectTreeOption<V>> = []
  const search = (items: Array<SelectTreeOption<V>>) => {
    for (const item of items) {
      if (predicate(item)) result.push(item)
      if (item.children) search(item.children)
    }
  }
  search(options)
  return result
}

// Note: Recursively filter options tree, keeping parents whose children match
export const filterOptionsTree = <V extends SelectOptionValue>(
  options: Array<SelectTreeOption<V>>,
  query: string,
): Array<SelectTreeOption<V>> => {
  const result: Array<SelectTreeOption<V>> = []
  for (const option of options) {
    const labelMatches = option.label.toLowerCase().includes(query)
    const filteredChildren = option.children ? filterOptionsTree(option.children, query) : []

    if (labelMatches || filteredChildren.length > 0) {
      result.push({
        ...option,
        ...(option.children && {
          children:
            labelMatches && filteredChildren.length === 0 ? option.children : filteredChildren,
        }),
      })
    }
  }
  return result
}

export const buildFolderTree = (folders: FolderItem[]): Array<SelectTreeOption<string>> => {
  const sorted = [...folders].sort((a, b) => a.position - b.position)

  const map = Object.fromEntries(
    sorted.map((folder) => [
      folder.id,
      {
        children: [] as Array<SelectTreeOption<string>>,
        label: folder.name,
        value: String(folder.id),
      },
    ]),
  )

  const roots: Array<SelectTreeOption<string>> = []

  Object.values(sorted).forEach((folder) => {
    const node = map[folder.id]

    if (folder.parentFolderId && map[folder.parentFolderId]) {
      map[folder.parentFolderId].children!.push(node)
    } else {
      roots.push(node)
    }
  })

  // Note: Remove empty children arrays for leaf nodes
  const clean = (options: Array<SelectTreeOption<string>>): Array<SelectTreeOption<string>> =>
    options.map(({ children, ...rest }) =>
      children && children.length > 0 ? { ...rest, children: clean(children) } : rest,
    )

  return clean(roots)
}

export const restoreMissingFromCache = <V extends SelectOptionValue>(
  selected: V[],
  present: Set<V>,
  cache: Map<V, SelectTreeOption<V>>,
): Array<FlattenedOption<V>> =>
  selected
    .filter((value) => !present.has(value) && cache.has(value))
    .map((value) => ({ depth: 0, hasChildren: false, option: cache.get(value)! }))

export const sortSelectedFirst = <V extends SelectOptionValue>(
  options: Array<FlattenedOption<V>>,
  value: V | V[] | null,
  isMulti: boolean,
): Array<FlattenedOption<V>> => {
  const isSelected = ({ option }: FlattenedOption<V>) =>
    isMulti ? (value as V[]).includes(option.value) : option.value === value
  return [...options].sort((a, b) => {
    const aSelected = isSelected(a)
    const bSelected = isSelected(b)
    if (aSelected && !bSelected) return -1
    if (!aSelected && bSelected) return 1
    return 0
  })
}

// --- Tree checkbox (tri-state) helpers -------------------------------------------------
//
// A node with children is a pure container: its own `value` is never part of the
// selection, its checkbox state is always *derived* from its selectable descendant
// leaves. A node without children is a leaf: it is the thing that actually gets
// selected/cleared. This keeps a single source of truth (the leaf values in
// `value`) instead of trying to keep parent + child selection state in sync by hand.

// Note: "selectable" excludes `disabled` leaves - a parent's checkbox only reflects
// (and only toggles) the leaves a user could actually check themselves.
export const collectSelectableLeafValues = <V extends SelectOptionValue>(
  option: SelectTreeOption<V>,
): V[] => {
  if (!option.children || option.children.length === 0) {
    return option.disabled ? [] : [option.value]
  }
  return option.children.flatMap(collectSelectableLeafValues)
}

export const getTreeCheckboxState = <V extends SelectOptionValue>(
  option: SelectTreeOption<V>,
  selected: ReadonlySet<V>,
): CheckboxState => {
  if (!option.children || option.children.length === 0) {
    return selected.has(option.value) ? 'checked' : 'unchecked'
  }

  const leaves = collectSelectableLeafValues(option)
  if (leaves.length === 0) return 'unchecked'

  const selectedCount = leaves.reduce((count, value) => count + (selected.has(value) ? 1 : 0), 0)
  if (selectedCount === 0) return 'unchecked'
  if (selectedCount === leaves.length) return 'checked'
  return 'indeterminate'
}

// Note: a container with no selectable leaves at all (every descendant disabled, or
// no descendants) has nothing for its checkbox to do.
export const isTreeOptionCheckboxDisabled = <V extends SelectOptionValue>(
  option: SelectTreeOption<V>,
): boolean => {
  if (option.disabled) return true
  if (!option.children || option.children.length === 0) return false
  return collectSelectableLeafValues(option).length === 0
}

// Note: toggling a leaf adds/removes just itself; toggling a parent selects all of
// its selectable descendants if any are unchecked, or clears all of them if every
// one is already checked (standard "select all / clear all" parent-checkbox behavior).
export const toggleTreeOption = <V extends SelectOptionValue>(
  option: SelectTreeOption<V>,
  currentValue: V[],
): V[] => {
  const leaves = collectSelectableLeafValues(option)
  if (leaves.length === 0) return currentValue

  const selected = new Set(currentValue)
  const allSelected = leaves.every((value) => selected.has(value))

  if (allSelected) {
    leaves.forEach((value) => selected.delete(value))
  } else {
    leaves.forEach((value) => selected.add(value))
  }

  return Array.from(selected)
}

// Note: recursively sorts every level of the tree so a checked or indeterminate node
// (per getTreeCheckboxState) floats above unchecked siblings - unlike sortSelectedFirst,
// which only matches a flat option's own value and can't see a container's derived state.
export const sortTreeSelectedFirst = <V extends SelectOptionValue>(
  options: Array<SelectTreeOption<V>>,
  selected: ReadonlySet<V>,
): Array<SelectTreeOption<V>> => {
  const hasSelection = (option: SelectTreeOption<V>) =>
    getTreeCheckboxState(option, selected) !== 'unchecked'

  const sortLevel = (items: Array<SelectTreeOption<V>>): Array<SelectTreeOption<V>> =>
    [...items]
      .map((item) => (item.children ? { ...item, children: sortLevel(item.children) } : item))
      .sort((a, b) => Number(hasSelection(b)) - Number(hasSelection(a)))

  return sortLevel(options)
}
